<template>
  <FormCard>
    <h2 class="font-bold">Assets</h2>
    <p class="mb-4">
      Assets are used to generate Social Boost creatives. You can add multiple sets of assets, with each set containing either a link to a
      post or a media file.
    </p>
    <ElForm ref="form" :model="form" :rules="formRules" @submit.prevent.native label-position="top" label-width="100px">
      <div v-for="(assetGroup, index) in form.assets" :key="index">
        <AssetItem
          :ref="'assetItem' + index"
          v-model="assetGroup.assets"
          :pos="index"
          :isOpen="assetGroup.isOpen"
          :name="assetGroup.name"
          @remove="removeSet(index)"
        />
      </div>
      <ElFormItem class="mb-4" prop="setName">
        <div class="flex items-center justify-between gap-1">
          <el-input v-model="form.setName" placeholder="Enter a name for this set" :maxlength="100" show-word-limit />
          <el-button type="primary" :disabled="!form.setName" icon="el-icon-plus" @click="addSet" plain>Add set</el-button>
        </div>
      </ElFormItem>
      <ElButton type="primary" class="w-full" :disabled="isDisabled" :loading="isSaving" @click="validateAllAssets">{{
        ctaActionText
      }}</ElButton>
    </ElForm>
  </FormCard>
</template>
<script>
import { mapGetters } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'
import FormCard from '@/components/social-products/social-boost/parts/FormCard.vue'
import AssetItem from '@/components/social-products/social-boost/forms/AssetItem.vue'
import { forEach } from 'lodash'

export default {
  name: 'Assets',
  components: {
    FormCard,
    AssetItem,
  },
  data() {
    return {
      isSaving: false,
      form: {
        assets: [],
        setName: '',
      },
      errors: {
        assets: '',
      },
      formRules: {
        setName: [
          {
            validator: (rule, value, callback) => {
              if (!value === false && !value.trim() === true) {
                callback(new Error('Set name cannot be just spaces.'))
              } else {
                callback()
              }
            },
            trigger: 'blur',
          },
        ],
      },
    }
  },
  computed: {
    ...mapGetters('socialBoostCampaign', ['campaign', 'flight']),
    ctaActionText() {
      switch (this.$route.name) {
        case 'social-boost-edit':
          return 'Update campaign'
        case 'social-boost-add-flight':
          return 'Create flight'
        case 'social-boost-add-assets':
          return 'Add assets'
        default:
          return 'Create campaign'
      }
    },
    isDisabled() {
      return this.isFlight ? false : this.form.assets.length === 0
    },
    isFlight() {
      switch (this.$route.name) {
        case 'social-boost-add-flight':
          return true
          break
        default:
          return false
      }
    },
  },
  watch: {
    campaign: {
      handler(newValue) {
        if (this.$route.name === 'social-boost-edit') {
          const campaignData = cloneDeep(newValue)
          const flight = campaignData.flights.length === 1 ? campaignData.flights[0] : null
          const assets = flight?.assets || []
          if (flight && assets.length > 0) {
            forEach(assets, (asset, index) => {
              this.form.assets.push({
                name: asset.name,
                isOpen: index === 0,
                assets: {
                  id: asset.id,
                  link: asset.post_url || null,
                  ctaColor: asset.cta_color || null,
                  ctaText: asset.cta_text || null,
                  landingPage: asset.landing_page || null,
                  mediaType: asset.post_url ? 'link' : 'upload',
                  files: asset?.media_file || [],
                  trackingTagsFiles: asset?.tracking_tags || [],
                },
              })
            })
          }
        }
      },
      immediate: true,
    },
  },
  methods: {
    addSet() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.form.assets.map((asset) => {
            asset.isOpen = false
            return asset
          })
          this.form.assets.push({
            name: this.form.setName,
            isOpen: true,
            assets: {
              mediaType: 'link',
            },
          })
          this.form.setName = ''
        }
      })
    },
    async validateAllAssets() {
      let allValid = true
      this.isSaving = true
      this.form.assets.forEach((_, index) => {
        const assetItem = this.$refs[`assetItem${index}`][0]
        const isUploadValide = assetItem.validateUploadFile()

        // console.log('assetItem', assetItem, isUploadValide)
        if (assetItem) {
          assetItem.validate((valid) => {
            if (!valid || !isUploadValide) {
              allValid = false
            }
          })
        }
      })
      this.$emit('validateForm', { step: 2, valid: allValid })
      if (allValid) {
        this.$store.commit('socialBoostCampaign/SET_CAMPAIGN_FORM', { section: 'assets', data: this.form.assets })
        try {
          let storeDispatch = ''
          let payload = ''

          switch (this.$route.name) {
            case 'social-boost-add-flight':
              storeDispatch = 'socialBoostCampaign/saveFlightForm'
              payload = { method: 'create' }
              break
            case 'social-boost-add-assets':
            case 'social-boost-create':
              storeDispatch = 'socialBoostCampaign/saveAssetsForm'
              payload = { flight_id: this.$route.params.flight_id || this.$route.query.flight || this.flight?.id, method: 'create' }
              break
            case 'social-boost-edit':
              const flight = this.flight?.assets?.length || 0
              if (flight === 0) {
                storeDispatch = 'socialBoostCampaign/saveAssetsForm'
                payload = { flight_id: this.flight?.id || this.$route?.query?.flight, method: 'create' }
              } else {
                storeDispatch = 'socialBoostCampaign/saveMultiAssetsForm'
                payload = { flight_id: this.flight?.id || this.$route?.query?.flight }
              }
              break
          }
          const res = await this.$store.dispatch(storeDispatch, payload)

          this.isSaving = false
          let routeData = {
            name: 'campaign-flight-view',
            params: { id: this.$route.query.campaign, flight_id: this.$route?.query?.flight || this.flight?.id },
          }

          switch (this.$route.name) {
            case 'social-boost-add-flight':
            case 'social-boost-add-assets':
              if (this.$route.query.redirect) {
                routeData = {
                  name: this.$route.query.redirect,
                  params: {
                    id: this.$route?.params?.id || this.$route?.query?.campaign,
                    flight_id: this.$route?.params?.flight_id || this.$route?.query?.flight,
                  },
                }
              } else {
                routeData = { name: 'view-campaign', params: { id: this.$route?.params?.id || this.$route.query.campaign } }
              }
              break
          }

          if (res) {
            this.$router.push(routeData)
          }
        } catch (err) {
          console.error('err', err)
          this.isSaving = false
        }
      } else {
        console.error('err')
        this.isSaving = false
      }
    },
    removeSet(index) {
      const id = this.form.assets[index]?.assets?.id
      if (id) {
        this.$store.commit('socialBoostCampaign/SET_DELETE_ASSETS_FORM', id)
      }
      this.form.assets.splice(index, 1)
    },
    submitForm() {
      this.$refs.form.validate((valid) => {
        if (valid) {
        } else {
          this.isError = true
        }
      })
    },
  },
}
</script>
